// Card 29, cables (R548, ui-map-interface.md §17.3.29).
//
// A chart draws a submarine cable as a magenta wavy line. Here the cable is
// the sonar share of the Comms ladder (R452 to R456): while an Oracle lends
// its array, a cable runs from that Oracle to every hull of the viewer's own
// squad, and a pulse walks along it so the reader sees which way the intel
// goes.
//
// **The receiver list is the server's, and the client does not hold it.**
// The wire says a hull broadcasts; it does not say who hears. So the piece
// draws the cable to the squad the VIEWER sails in, and the legend says
// exactly that: `Oracle → squad`. It is not a claim about anybody else's
// squad, and it must never grow into one.
//
// The historical reading is the courier's route: a dotted line with an
// arrow every seventy px.

import { chart as S } from '../../../gen/strings';
import type { CHART } from '../../../gen/presentation';
import type { FrameInfo } from '../../layer';
import { CHART_INK, faceOf } from './shared';
import {
  type ChartContext, type ChartFleet, type ChartSinks, type Piece,
} from '../context';

export class CablesPiece implements Piece {
  readonly id = 'cables' as const;
  readonly tier = 'screen' as const;
  readonly pack = 'live' as const;
  readonly film = 'hide' as const;
  readonly rebuildOn = [] as const;

  rebuild(_ctx: ChartContext): void { /* the shares are read at every pack */ }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    const layer = ctx.scene.fleets;
    if (!layer) return;
    const hist = ctx.reading === 'historical';
    const ink = hist ? CHART_INK.pale : CHART_INK.magenta;
    const fleets = layer.chartFleets();
    // The rung the clan sails at. It is the clan's best Signal Station
    // level, which is the figure the ladder of R452 is read off.
    const rung = ctx.state.clan?.comms ?? 0;
    for (const a of fleets) {
      if (!a.sharing || !a.own) continue;
      for (const b of fleets) {
        if (b.id === a.id || !b.squad) continue;
        this.cable(sinks, ctx, a, b, ink, hist, rung);
      }
    }
  }

  // -- the private half ------------------------------------------------------

  /** One cable, from the Oracle to one hull of the viewer's own squad. */
  private cable(
    sinks: ChartSinks, ctx: ChartContext,
    a: ChartFleet, b: ChartFleet, ink: number, hist: boolean, rung: number,
  ): void {
    const t = ctx.cfg.pieces.cables;
    const fig = t.fig;
    const r = t[ctx.reading];
    const dx = b.x - a.x, dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    // Two hulls in the same water carry no cable: the stand-off at each end
    // would leave nothing to draw.
    if (len <= fig.stand_off * 2) return;
    const ux = dx / len, uy = dy / len;
    const s0 = fig.stand_off, s1 = len - fig.stand_off;
    if (hist) {
      sinks.segments.line(
        a.x + ux * s0, a.y + uy * s0, a.x + ux * s1, a.y + uy * s1,
        r.width, ink, r.alpha, 0, r.dash[0], r.dash[1],
      );
      for (let s = s0 + fig.arrow_every; s < s1; s += fig.arrow_every) {
        this.arrow(sinks, fig, a.x + ux * s, a.y + uy * s, ux, uy, ink, r.alpha, r.width);
      }
    } else {
      // The wave: one short segment every `step_px` of the run, offset
      // along the normal by a sine of the arc length.
      let px = 0, py = 0, have = false;
      for (let s = s0; s <= s1; s += fig.step_px) {
        const off = Math.sin(s / fig.wave_len) * fig.wave_amp;
        const x = a.x + ux * s - uy * off;
        const y = a.y + uy * s + ux * off;
        if (have) sinks.segments.line(px, py, x, y, r.width, ink, fig.alpha);
        px = x; py = y; have = true;
      }
      // The walking pulse: which way the intel goes.
      const u = (ctx.timeS * fig.pulse_hz) % 1;
      const at = s0 + u * (s1 - s0);
      const qx = a.x + ux * at, qy = a.y + uy * at;
      sinks.circles.disc(qx, qy, fig.pulse_r, ink, r.alpha);
      sinks.circles.disc(qx, qy, fig.halo_r, ink, fig.halo_alpha);
    }
    const text = hist ? S.cable_historical : S.cable_modern({ rung });
    ctx.glyphs.word(
      sinks.quads, text,
      (a.x + b.x) / 2, (a.y + b.y) / 2 - fig.label_lift,
      faceOf(ctx.reading), t.text.main, ink, r.alpha,
      { anchor: 'centre' },
    );
  }

  /** The courier's direction mark: a shaft with two feathers. */
  private arrow(
    sinks: ChartSinks, fig: typeof CHART.pieces.cables.fig,
    x: number, y: number, ux: number, uy: number,
    ink: number, alpha: number, width: number,
  ): void {
    const half = fig.arrow_len / 2;
    const hx = x + ux * half, hy = y + uy * half;
    sinks.segments.line(x - ux * half, y - uy * half, hx, hy, width, ink, alpha);
    // The head, as two barbs off the point rather than a filled triangle:
    // the batches carry no filled path.
    const back = fig.arrow_head, side = fig.arrow_side;
    for (let s = 1; s >= -1; s -= 2) {
      sinks.segments.line(
        hx, hy,
        hx - ux * back - uy * side * s, hy - uy * back + ux * side * s,
        width, ink, alpha,
      );
    }
  }
}

export const cables = new CablesPiece();
